// ui/gameReducer.js: pure reducer for the hot-seat game loop (M2.c-10b).
//
// Per plan-m2 §M2.c-10b: useReducer with action types mirroring the
// Lua state machine. The reducer itself is PURE, it never calls
// LuaGameState. Mutating actions (DEAL/SUBMIT_BID/...) get queued into
// `pendingCommand`; the useGameLoop hook drains that field by calling
// the corresponding LuaGameState method and then dispatching
// COMMAND_APPLIED with the new snapshot (or COMMAND_FAILED on error).
//
// Why the split: the reducer is easy to unit-test without spinning a
// wasmoon engine. The hook owns the impure I/O. Each layer can be
// reasoned about independently; the reducer's idempotency property
// (same input → same state) is preserved because all impure work lives
// in useEffect.
//
// State shape (bounded, no unbounded arrays):
//   {
//     snapshot:       object|null   - last applied snapshot from Lua
//     pendingCommand: Command|null  - in-flight command; null when idle
//     lastResult:     {ok,...}|null - result of the last APPLIED command
//     error:          string|null   - most recent failure string (cleared
//                                      on next command queue)
//     generation:     number        - bumps once per APPLIED; used by
//                                      effect dependencies to detect
//                                      cache invalidation
//   }

// ---- Action type constants ----

// Commands the player / bot scheduler may queue. Each is also the
// reducer-action `type` field for that command.
export const ACTIONS = Object.freeze({
  DEAL:            'DEAL',
  SUBMIT_BID:      'SUBMIT_BID',
  DEALER_DISCARD:  'DEALER_DISCARD',
  PLAY_CARD:       'PLAY_CARD',
  SCORE:           'SCORE',
  RESET_GAME:      'RESET_GAME',
  NEXT_HAND:       'NEXT_HAND',
});

// Bare string for callers that don't want to import ACTIONS just to
// reference one type. Stays in sync via the test below.
export const NEXT_HAND_ACTION_TYPE = ACTIONS.NEXT_HAND;

// Internal lifecycle actions dispatched by the hook itself.
export const LIFECYCLE = Object.freeze({
  COMMAND_APPLIED: 'COMMAND_APPLIED',
  COMMAND_FAILED:  'COMMAND_FAILED',
  SET_SNAPSHOT:    'SET_SNAPSHOT',  // initial snapshot load
});

/** @type {Set<string>} */
const COMMAND_TYPES = new Set(Object.values(ACTIONS));

export const INITIAL_STATE = Object.freeze({
  snapshot:       null,
  pendingCommand: null,
  lastResult:     null,
  error:          null,
  generation:     0,
});

/**
 * Pure reducer. Side effects forbidden. Same input always produces the
 * same output.
 *
 * @param {object} state
 * @param {{type: string, [k:string]: any}} action
 * @returns {object}
 */
export function gameReducer(state, action) {
  if (!action || typeof action.type !== 'string') return state;

  if (COMMAND_TYPES.has(action.type)) {
    // Queue a command. We deliberately overwrite an in-flight pending
    // command, the hook + bot scheduler are responsible for preventing
    // double-queue races (M2.c-10c handles cancellation by action_id).
    // The dispatch-twice-same-action case is idempotent: the same
    // command object lands in pendingCommand both times.
    return {
      ...state,
      pendingCommand: action,
      error: null,
    };
  }

  switch (action.type) {
    case LIFECYCLE.COMMAND_APPLIED:
      return {
        ...state,
        pendingCommand: null,
        snapshot: action.snapshot ?? state.snapshot,
        lastResult: action.result ?? null,
        error: null,
        generation: state.generation + 1,
      };

    case LIFECYCLE.COMMAND_FAILED:
      return {
        ...state,
        pendingCommand: null,
        // Preserve the last good snapshot, a failed command means Lua
        // either rejected the action (state unchanged) or threw (state
        // may be untrustworthy but rolling back is M2.c-10e's job).
        error: action.error || 'unknown error',
      };

    case LIFECYCLE.SET_SNAPSHOT:
      // Initial snapshot load on mount, doesn't go through the command
      // queue because there's nothing to dispatch yet. The hook fires
      // this exactly once when LuaGameState attaches.
      return {
        ...state,
        snapshot: action.snapshot ?? null,
        generation: state.generation + 1,
      };

    default:
      // Unknown action, return state unchanged. Per Redux convention.
      return state;
  }
}

// Convenience constructors for actions. Lets callers / tests express
// "deal seed 42" without remembering the literal shape.
export const actionCreators = Object.freeze({
  deal:           (seed)            => ({ type: ACTIONS.DEAL, seed }),
  submitBid:      (seat, bid)       => ({ type: ACTIONS.SUBMIT_BID, seat, bid }),
  dealerDiscard:  (cardId)          => ({ type: ACTIONS.DEALER_DISCARD, cardId }),
  playCard:       (seat, cardId)    => ({ type: ACTIONS.PLAY_CARD, seat, cardId }),
  score:          ()                => ({ type: ACTIONS.SCORE }),
  resetGame:      (settings = {})   => ({ type: ACTIONS.RESET_GAME, settings }),
  nextHand:
    /** @param {1|2|3|4} nextDealerSeat @param {number} nextSeed */
    (nextDealerSeat, nextSeed) =>
      ({ type: ACTIONS.NEXT_HAND, nextDealerSeat, nextSeed }),
  commandApplied:
    /** @param {object} snapshot @param {object|null} [result] */
    (snapshot, result = null) =>
      ({ type: LIFECYCLE.COMMAND_APPLIED, snapshot, result }),
  commandFailed:  (error)           => ({ type: LIFECYCLE.COMMAND_FAILED, error }),
  setSnapshot:    (snapshot)        => ({ type: LIFECYCLE.SET_SNAPSHOT, snapshot }),
});
